import { throttle } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';

/**
 * Returns a throttled version of a changing value.
 *
 * Updates are limited to at most once per `delay` milliseconds. The latest
 * pending value is still applied at the end of the throttle window.
 */
export function useThrottledValue<T>(value: T, delay: number): T {
  const [throttledValue, setThrottledValue] = useState(value);

  // Recreate the throttled setter only when the delay changes.
  const throttledUpdater = useMemo(
    () =>
      throttle(setThrottledValue, delay, { leading: false, trailing: true }),
    [delay],
  );

  // Feed every incoming value through the throttled setter.
  useEffect(() => {
    throttledUpdater(value);
  }, [throttledUpdater, value]);

  // Cancel any pending trailing update when the throttled setter changes or
  // the component unmounts, preventing state updates after unmount.
  useEffect(() => {
    return () => throttledUpdater.cancel();
  }, [throttledUpdater]);

  return throttledValue;
}
